import { useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { getReactQueryAxiosInstance } from './api';
import { endpoints } from './endpoints';

export type WritebackStatus =
  | 'pending'
  | 'in_progress'
  | 'success'
  | 'error'
  | 'not_pushed';

export interface WritebackDocumentStatus {
  documentId: string;
  fileName: string;
  status: WritebackStatus;
  error?: string;
}

export interface WritebackStatusResponse {
  status: WritebackStatus;
  documents: WritebackDocumentStatus[];
}

/**
 * Query key factory for writeback status
 */
export const writebackStatusKeys = {
  all: ['writeback-status'] as const,
  detail: (batchId: string, unifiedAppointmentId: string) =>
    [...writebackStatusKeys.all, batchId, unifiedAppointmentId] as const,
};

/**
 * React Query hook for polling writeback status.
 *
 * Queries by batchId (after a push) or unifiedAppointmentId (initial page load).
 * Polls every 5 seconds while status is pending or in_progress.
 * Stops polling when status becomes success, error, or not_pushed,
 * or after ~2 minutes of stuck pending.
 *
 * @param batchId - The batch ID returned from the writeback endpoint (null to disable)
 * @param unifiedAppointmentId - The appointment ID for initial status lookup (null to disable)
 * @returns React Query result with WritebackStatusResponse
 */
export const useWritebackStatus = (
  batchId: string | null,
  unifiedAppointmentId?: string | null,
) => {
  const pendingPollCount = useRef(0);

  // Reset counter when query identity changes (new push or different appointment)
  useEffect(() => {
    pendingPollCount.current = 0;
  }, [batchId, unifiedAppointmentId]);

  const user = useSelector(
    (state: {
      login?: {
        user?: { primaryPracticeId?: string; primaryOrganizationId?: string };
      };
    }) => state?.login?.user,
  );
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;

  return useQuery({
    queryKey: writebackStatusKeys.detail(
      batchId ?? '',
      unifiedAppointmentId ?? '',
    ),
    queryFn: async (): Promise<WritebackStatusResponse> => {
      if (
        !(batchId || unifiedAppointmentId) ||
        !currentOrganizationId ||
        !currentPracticeId
      ) {
        throw new Error('Missing required parameters');
      }

      const instance = getReactQueryAxiosInstance();
      const response = await instance.request<WritebackStatusResponse>({
        method: 'GET',
        url: `/${endpoints.claimWritebackStatus}`,
        headers: {
          Practice: currentPracticeId,
          Org: currentOrganizationId,
        },
        params: { batchId: batchId ?? undefined, unifiedAppointmentId: unifiedAppointmentId ?? undefined },
      });

      return response.data;
    },
    enabled: Boolean(
      (batchId || unifiedAppointmentId) &&
        currentPracticeId &&
        currentOrganizationId,
    ),
    refetchInterval: (data) => {
      // Terminal states — stop polling
      if (
        data?.status === 'success' ||
        data?.status === 'error' ||
        data?.status === 'not_pushed'
      ) {
        pendingPollCount.current = 0;
        return false;
      }
      // Actively processing — reset counter
      if (data?.status === 'in_progress') {
        pendingPollCount.current = 0;
      }
      pendingPollCount.current++;
      // Stop after ~2 min of pending (24 × 5s)
      if (pendingPollCount.current > 24) return false;
      return 5000;
    },
    staleTime: 0, // Always consider data stale to enable polling
  });
};
